import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { createUserProfile, getUserProfile, updateUserProfile, type UserProfile } from '../services/userService';

interface AuthContextValue {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      
      if (u) {
        try {
          // Try to get existing user profile
          let profile = await getUserProfile(u.uid);
          
          // If no profile exists, create one
          if (!profile) {
            const newProfile: UserProfile = {
              uid: u.uid,
              name: u.displayName || '',
              username: u.displayName?.toLowerCase().replace(/\s+/g, '') || '',
              email: u.email || '',
              avatarUrl: u.photoURL || '',
              createdAt: new Date(),
              updatedAt: new Date()
            };
            
            await createUserProfile(newProfile);
            profile = newProfile;
          }
          
          setUserProfile(profile);
        } catch (error) {
          console.error('Error handling user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const updateProfile = useCallback(async (profileData: Partial<UserProfile>) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await updateUserProfile(user.uid, profileData);
      const updatedProfile = await getUserProfile(user.uid);
      setUserProfile(updatedProfile);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  }, [user]);

  const value = useMemo(() => ({ 
    user, 
    userProfile, 
    loading, 
    updateProfile, 
    refreshProfile 
  }), [user, userProfile, loading, updateProfile, refreshProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};


